import React from 'react';
import {
  AfterSubmitFunc,
  Button,
  ButtonColor,
  FormTemplate,
  TextInput,
} from 'ui-builder';
import {
  ProjectPlanOfTypeDropdown,
  ProjectTypeDropdown,
  SecondaryWorkflowsDropdown,
  TeamSetupAreaIdDropdown
} from 'features/tasks-forms';
import {
  CreateProjectFromContractRequest,
  FileType,
  Project,
  TeamPermission,
  useProjectFromContractCreator,
} from 'nekst-api';
import useLaunchFromContractNavigator from './launchFromContractNavigator';
import { FileUploaderInput, Popup, PopupText } from 'features/nekst-widgets';

import { useAuthorizationChecker } from 'authorization-scope';
import useSettingsNavigator from '../../../settings/settingsNavigator';
import useFeatureToggleService, { MultiWorkflowToggleWrapper, ToggledFeature } from 'feature-toggle';
import { AiAnalysisLoaderWidget } from '../Shared/AiAnalysisLoaderWidget';

function GoToBillingButton() {
  const navigator = useSettingsNavigator();

  return (
    <Button
      text="Go to Billing"
      color={ButtonColor.GREEN}
      isFilled
      onClick={() => {
        navigator.openBillingPage();
      }}
    />
  );
}

export function ImportFromContractWarning() {
  return (
    <Popup
      title="You need to upgrade your subscription"
      content={(
        <PopupText>
          Please upgrade to a current Pro Plan in order to access this feature. Your
          current billing plan only allow for manual entry of transactions.
        </PopupText>
      )}
      buttons={<GoToBillingButton />}
    />
  );
}

export function ImportFromContractForm(props: {
  afterSubmitFunc?: AfterSubmitFunc,
}) {
  const projectCreator = useProjectFromContractCreator();

  const navigator = useLaunchFromContractNavigator();

  return (
    <FormTemplate<CreateProjectFromContractRequest, Project>
      title="Import from contract"
      submitFormFunc={async (request: CreateProjectFromContractRequest) => {
        return await projectCreator.createProjectFromContract(request);
      }}
      afterSubmitFunc={async (data) => {
        navigator.openVerifyContractDataPage(data.id);

        await props.afterSubmitFunc?.(data);
      }}
      validationSchema={{
        _setupArea: {
          type: 'object',
          constraints: {
            required: true,
          },
          properties: {
            id: {
              type: 'int',
              constraints: {
                required: true,
              }
            }
          }
        },
        name: {
          type: 'string',
          constraints: {
            required: true,
            maxLength: 60,
          }
        },
        type: {
          type: 'string',
          constraints: {
            required: true,
          }
        },
        planId: {
          type: 'int',
          constraints: {
            required: true,
          }
        },
        fileId: {
          type: 'int',
          constraints: {
            required: true,
          }
        },
      }}
    >
      <TeamSetupAreaIdDropdown source="_setupArea.id" />
      <TextInput source="name" label="Transaction Name" />
      <ProjectTypeDropdown source="type" />
      <ProjectPlanOfTypeDropdown
        projectTypeSource="type"
        source="planId"
        label="Primary Workflow"
        setupAreaIdSource="_setupArea.id"
        withContractTemplateAssigned
        withKeyDatesOnly
      />
      <MultiWorkflowToggleWrapper>
        <SecondaryWorkflowsDropdown
          source="secondaryPlanIds"
          label="Secondary Workflows"
          projectTypeSource="type"
          primaryPlanSource="planId"
          setupAreaIdSource="_setupArea.id"
        />
      </MultiWorkflowToggleWrapper>
      <FileUploaderInput
        fileType={FileType.PROJECT_DOCUMENT}
        fileDataSource="_file"
        source="fileId"
        label="Contract Document"
      />
      <AiAnalysisLoaderWidget />
    </FormTemplate>
  );
}

export default function ImportFromContractFormWrapper(
  props: {
    afterSubmitFunc?: AfterSubmitFunc,
  }
) {
  const { isGranted } = useAuthorizationChecker();

  const featureToggleService = useFeatureToggleService();

  if (isGranted(TeamPermission.LAUNCH_FROM_CONTRACT) || featureToggleService.isFeatureEnabled(ToggledFeature.NEW_TRANSACTIONS_MANAGEMENT)) {
    return <ImportFromContractForm afterSubmitFunc={props.afterSubmitFunc} />;
  } else {
    return <ImportFromContractWarning />;
  }
}
